'use client';

import { SearchspringService } from 'components/searchspring/searchspringService';
import { SearchResponseModel, SearchspringState } from 'int-searchspring-api';
import HashStatic from 'object-hash';
import { useEffect, useRef } from 'react';
import { Fetcher } from 'swr';
import useSWRInfinite from 'swr/infinite';

// A function to get the SWR key of each page,
// its return value will be accepted by `fetcher`.
// If `null` is returned, the request of that page won't start.
const getKey = (state: Partial<SearchspringState>, pageIndex: number, previousPageData: SearchResponseModel | null) => {
  if (!state) return null;
  const page = pageIndex + 1;
  if (previousPageData && !previousPageData.results?.length) return null; // reached the end

  return [state, page];
}

const fetcher: Fetcher<SearchResponseModel, any> = async (args: any) => {
  const [state, page] = args;
  const searchState = { ...state, page, domain: window.location.href };
  const searchService = new SearchspringService(searchState);
  const result = await searchService.searchGET(searchState);
  const data = result.body;
  return data || {};
};

export default function useSearchSpring({ state }: { state?: SearchspringState }) {
  const { page, domain, ...searchState } = state ?? {};
  const prevSearchState = useRef(searchState);

  const {
    data,
    mutate,
    error,
    isLoading,
    isValidating,
    size,
    setSize
  } = useSWRInfinite(getKey.bind(null, searchState), fetcher, {
    initialSize: 1,
    revalidateFirstPage: false,
    persistSize: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: true
  });

  // Detect filter changes
  useEffect(() => {
    if (HashStatic(searchState) !== HashStatic(prevSearchState.current)) {
      mutate();
      prevSearchState.current = searchState;
    }
  }, [searchState, mutate])

  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === "undefined");
  const isUpdating = isValidating && data && data.length === size;

  const resultsCache = useRef(data || []);
  // Persist current view until loading has completed
  if (data && !isLoading) {
    resultsCache.current = data;
  }

  const allResults = resultsCache.current.flatMap(page => page.results);
  const currentPage = resultsCache.current.at(size - 1);

  if (error) {
    console.log(error);
  }

  return {
    isLoading: isLoadingMore,
    isUpdating,
    data: resultsCache.current,
    allResults,
    currentPage,
    mutate,
    page: size,
    setPage: setSize
  };
}
